import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import { Swords, ArrowLeft, MessageCircle, Send, Users, Zap } from 'lucide-react';

const getSportEmoji = (category = '') => {
  const c = (category || '').toLowerCase();
  if (c.includes('soccer')) return '⚽';
  if (c.includes('basketball') || c.includes('nba')) return '🏀';
  if (c.includes('baseball') || c.includes('mlb')) return '⚾';
  if (c.includes('hockey') || c.includes('nhl')) return '🏒';
  if (c.includes('tennis')) return '🎾';
  if (c.includes('volleyball')) return '🏐';
  if (c.includes('rugby')) return '🏉';
  if (c.includes('golf')) return '⛳';
  if (c.includes('football') || c.includes('nfl')) return '🏈';
  return '⚔️';
};

const fmtNum = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const CREDIT_MAX = 100;
const COOLDOWN_SECS = 60;
const CREDIT_KEY = 'rc_credits_v1';
const COOLDOWN_END_KEY = 'rc_cooldown_end_v1';

function loadCredits() {
  try { const s = localStorage.getItem(CREDIT_KEY); const c = s ? parseInt(s, 10) : CREDIT_MAX; return Number.isFinite(c) && c >= 0 ? c : CREDIT_MAX; }
  catch { return CREDIT_MAX; }
}
function saveCredits(n) { try { localStorage.setItem(CREDIT_KEY, String(n)); } catch {} }
function loadCooldownEnd() { try { const v = localStorage.getItem(COOLDOWN_END_KEY); return v ? parseInt(v, 10) : null; } catch { return null; } }
function saveCooldownEnd(ts) { try { if (ts) localStorage.setItem(COOLDOWN_END_KEY, String(ts)); else localStorage.removeItem(COOLDOWN_END_KEY); } catch {} }

function HeartParticle({ id, x, color, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 1200); return () => clearTimeout(t); }, [onDone]);
  return <div className="heart-particle" style={{ left: x, color }}>{color === '#0EA5E9' ? '🔵' : '🔴'}</div>;
}

function PulsingDot() { return <span className="pulsing-dot" />; }

export default function BattlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [liveVotes, setLiveVotes] = useState(null);
  const [votedTeam, setVotedTeam] = useState(null);
  const [voting, setVoting] = useState(false);
  const [voteError, setVoteError] = useState('');

  const [credits, setCredits] = useState(() => loadCredits());
  const [cooldownSecs, setCooldownSecs] = useState(0);
  const cooldownRef = useRef(null);

  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [posting, setPosting] = useState(false);

  const [hearts, setHearts] = useState([]);
  const [rematchLoading, setRematchLoading] = useState(false);
  const [rematchDone, setRematchDone] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const wsRef = useRef(null);
  const commentsBottomRef = useRef(null);

  // ── Cooldown from localStorage ─────────────────────────────────
  const startCountdown = useCallback((endTs) => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    const tick = () => {
      const left = Math.ceil((endTs - Date.now()) / 1000);
      if (left <= 0) {
        clearInterval(cooldownRef.current);
        cooldownRef.current = null;
        setCredits(CREDIT_MAX); setCooldownSecs(0);
        saveCredits(CREDIT_MAX); saveCooldownEnd(null);
      } else {
        setCooldownSecs(left);
      }
    };
    tick();
    cooldownRef.current = setInterval(tick, 1000);
  }, []);

  useEffect(() => {
    const end = loadCooldownEnd();
    if (end && end > Date.now()) {
      const secs = Math.ceil((end - Date.now()) / 1000);
      setCooldownSecs(secs);
      startCountdown(end);
    } else if (end) {
      saveCredits(CREDIT_MAX); saveCooldownEnd(null);
    }
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, [startCountdown]);

  // ── Load challenge + comments ──────────────────────────────────
  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true); setError('');
      try {
        const [chal, cmts] = await Promise.all([
          api.getChallenge(token, id),
          api.getChallengeComments(token, id).catch(() => []),
        ]);
        setChallenge(chal);
        setComments(Array.isArray(cmts) ? cmts : cmts?.results ?? []);
      } catch {
        setError('Could not load challenge.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, token]);

  // ── WebSocket for live vote updates ───────────────────────────
  useEffect(() => {
    if (!id) return;
    let retryDelay = 2000;
    let retryTimer = null;
    const connect = () => {
      try {
        const wsUrl = token
          ? `wss://chat.meanc.net/ws/challenge/?token=${token}`
          : 'wss://chat.meanc.net/ws/challenge/';
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'challenge_update' && String(msg.challenge?.id) === String(id)) {
              const c = msg.challenge;
              setLiveVotes(prev => ({
                teamA_likes: c.teamA_likes ?? prev?.teamA_likes,
                teamB_likes: c.teamB_likes ?? prev?.teamB_likes,
              }));
            }
          } catch {}
        };
        ws.onopen = () => { retryDelay = 2000; };
        ws.onerror = () => ws.close();
        ws.onclose = () => {
          wsRef.current = null;
          retryTimer = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 60000);
        };
      } catch {}
    };
    connect();
    return () => { if (retryTimer) clearTimeout(retryTimer); wsRef.current?.close(); };
  }, [id, token]);

  useEffect(() => {
    if (showComments) commentsBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments, showComments]);

  const isLive = challenge
    ? ['live', 'ongoing', 'started'].includes(String(challenge.status || '').toLowerCase()) || challenge.is_live === true
    : false;
  const isEnded = challenge
    ? ['ended', 'completed', 'finished', 'done'].includes(String(challenge.status || '').toLowerCase())
    : false;

  const colorA = challenge?.teamA_color || '#0EA5E9';
  const colorB = challenge?.teamB_color || '#EF4444';
  const teamALikes = liveVotes?.teamA_likes ?? challenge?.teamA_likes ?? 0;
  const teamBLikes = liveVotes?.teamB_likes ?? challenge?.teamB_likes ?? 0;
  const total = teamALikes + teamBLikes;
  const pA = total ? Math.round((teamALikes / total) * 100) : 50;
  const pB = 100 - pA;
  const sportEmoji = challenge ? getSportEmoji(challenge.category_name || challenge.category) : '⚔️';
  const winner = isEnded ? (teamALikes >= teamBLikes ? 'A' : 'B') : null;

  // ── Vote ───────────────────────────────────────────────────────
  const handleVote = async (side) => {
    if (!token) { setVoteError('Sign in to vote.'); return; }
    if (isEnded) { setVoteError('This battle is over.'); return; }
    if (votedTeam && votedTeam !== side) { setVoteError('You already backed the other team!'); return; }
    if (voting || cooldownSecs > 0) return;

    const teamId = side === 'a' ? challenge.teamA : challenge.teamB;
    setVoting(true); setVoteError('');

    const heartId = Date.now();
    setHearts(prev => [...prev, { id: heartId, x: side === 'a' ? '22%' : '68%', color: side === 'a' ? colorA : colorB }]);

    try {
      const data = await api.vote(token, challenge.id, teamId, 1);
      const v = data?.payload ?? data;
      if (v?.team_a_votes != null || v?.teamA_likes != null) {
        setLiveVotes({
          teamA_likes: v.team_a_votes ?? v.teamA_likes ?? teamALikes,
          teamB_likes: v.team_b_votes ?? v.teamB_likes ?? teamBLikes,
        });
      }
      setVotedTeam(side);

      let newC;
      setCredits(prev => { newC = Math.max(0, prev - 1); saveCredits(newC); return newC; });
      setTimeout(() => {
        if (newC <= 0) {
          const endTs = Date.now() + COOLDOWN_SECS * 1000;
          saveCooldownEnd(endTs); setCooldownSecs(COOLDOWN_SECS); startCountdown(endTs);
        }
      }, 0);
    } catch { setVoteError('Vote failed — try again.'); }
    finally { setVoting(false); }
  };

  // ── Comment ────────────────────────────────────────────────────
  const handleComment = async () => {
    const text = commentInput.trim();
    if (!text || !token || posting) return;
    setPosting(true);
    const optId = `opt_${Date.now()}`;
    setComments(prev => [...prev, { id: optId, comment: text, user: { username: user?.username || 'You' }, created_at: new Date().toISOString() }]);
    setCommentInput('');
    try {
      const saved = await api.postChallengeComment(token, challenge.id, text);
      setComments(prev => prev.map(c => c.id === optId ? (saved || c) : c));
    } catch {
      setComments(prev => prev.filter(c => c.id !== optId));
    }
    setPosting(false);
  };

  // ── Rematch ────────────────────────────────────────────────────
  const handleRematch = async () => {
    if (!token || rematchLoading || rematchDone) return;
    setRematchLoading(true);
    try { await api.requestRematch(token, challenge.id); setRematchDone(true); } catch {}
    setRematchLoading(false);
  };

  if (loading) return (
    <div className="battle-page">
      <div className="battle-loading">
        <Swords size={48} color="rgba(14,165,233,0.4)" />
        <p>Loading battle…</p>
      </div>
    </div>
  );

  if (error || !challenge) return (
    <div className="battle-page">
      <div className="feed-error">
        <Swords size={40} color="rgba(239,68,68,0.4)" />
        <p>{error || 'Challenge not found.'}</p>
        <button className="auth-btn" onClick={() => navigate('/app/feed')} style={{ width: 'auto', padding: '10px 24px' }}>← Back to Rivalries</button>
      </div>
    </div>
  );

  const commentAuthor = (c) => c?.user?.username ?? c?.author?.username ?? c?.username ?? 'Fan';
  const commentText = (c) => c?.comment ?? c?.text ?? c?.body ?? '';

  return (
    <div className="battle-page">
      {/* Heart particles */}
      {hearts.map(h => (
        <HeartParticle key={h.id} id={h.id} x={h.x} color={h.color} onDone={() => setHearts(prev => prev.filter(p => p.id !== h.id))} />
      ))}

      {/* Header */}
      <div className="battle-header">
        <button className="battle-back-btn" onClick={() => navigate('/app/feed')}>
          <ArrowLeft size={20} />
        </button>
        <div className="battle-header-center">
          <span className="battle-category">{(challenge.category_name || challenge.category || 'RIVALRY').toUpperCase()}</span>
          {isLive ? (
            <span className="battle-status-pill battle-status-live"><PulsingDot /> LIVE</span>
          ) : isEnded ? (
            <span className="battle-status-pill battle-status-ended">ENDED</span>
          ) : (
            <span className="battle-status-pill battle-status-upcoming">UPCOMING</span>
          )}
        </div>
        <div className="battle-credit-chip">
          {cooldownSecs > 0
            ? <span style={{ color: '#F97316', fontWeight: 700, fontSize: 12 }}>⏳{cooldownSecs}s</span>
            : <><Zap size={13} color="#22C55E" /><span style={{ color: '#22C55E', fontWeight: 700, fontSize: 12 }}>{credits}</span></>
          }
        </div>
      </div>

      {/* Arena */}
      <div className="battle-arena" style={{ background: `linear-gradient(160deg, ${colorA}1A 0%, #0D1117 50%, ${colorB}1A 100%)` }}>

        {/* Winner banner */}
        {isEnded && winner && (
          <div className="battle-winner-banner" style={{ color: winner === 'A' ? colorA : colorB, borderColor: `${winner === 'A' ? colorA : colorB}44` }}>
            🏆 {winner === 'A' ? challenge.teamA_name : challenge.teamB_name} wins this round!
          </div>
        )}

        <div className="battle-teams-row">
          {/* Team A */}
          <div className="battle-team-col">
            <div className="battle-team-logo" style={{ borderColor: `${colorA}66`, boxShadow: `0 0 40px ${colorA}44`, background: `${colorA}18` }}>
              <span style={{ fontSize: 44 }}>{sportEmoji}</span>
            </div>
            <h2 className="battle-team-name">{challenge.teamA_name || 'Team A'}</h2>
            <div className="battle-vote-count" style={{ color: colorA }}>{fmtNum(teamALikes)}</div>
            <div className="battle-pct-badge" style={{ background: `${colorA}22`, color: colorA }}>{pA}%</div>
            {isLive && (
              <button
                className={`battle-vote-btn ${votedTeam === 'a' ? 'battle-vote-btn--voted' : ''}`}
                style={{
                  '--btn-color': colorA,
                  background: votedTeam === 'a' ? colorA : 'transparent',
                  borderColor: colorA,
                  color: votedTeam === 'a' ? '#fff' : colorA,
                  opacity: votedTeam === 'b' ? 0.35 : 1,
                }}
                disabled={voting || votedTeam === 'b' || cooldownSecs > 0}
                onClick={() => handleVote('a')}
              >
                {votedTeam === 'a' ? '✓ Voted!' : '⚡ Vote'}
              </button>
            )}
          </div>

          {/* VS divider */}
          <div className="battle-vs-col">
            <div className="battle-vs-orb">
              <Swords size={22} color="#fff" />
            </div>
            <span className="battle-vs-label">VS</span>
            <span className="battle-total-label"><Users size={11} /> {fmtNum(total)}</span>
          </div>

          {/* Team B */}
          <div className="battle-team-col">
            <div className="battle-team-logo" style={{ borderColor: `${colorB}66`, boxShadow: `0 0 40px ${colorB}44`, background: `${colorB}18` }}>
              <span style={{ fontSize: 44, transform: 'scaleX(-1)', display: 'inline-block' }}>{sportEmoji}</span>
            </div>
            <h2 className="battle-team-name">{challenge.teamB_name || 'Team B'}</h2>
            <div className="battle-vote-count" style={{ color: colorB }}>{fmtNum(teamBLikes)}</div>
            <div className="battle-pct-badge" style={{ background: `${colorB}22`, color: colorB }}>{pB}%</div>
            {isLive && (
              <button
                className={`battle-vote-btn ${votedTeam === 'b' ? 'battle-vote-btn--voted' : ''}`}
                style={{
                  '--btn-color': colorB,
                  background: votedTeam === 'b' ? colorB : 'transparent',
                  borderColor: colorB,
                  color: votedTeam === 'b' ? '#fff' : colorB,
                  opacity: votedTeam === 'a' ? 0.35 : 1,
                }}
                disabled={voting || votedTeam === 'a' || cooldownSecs > 0}
                onClick={() => handleVote('b')}
              >
                {votedTeam === 'b' ? '✓ Voted!' : '⚡ Vote'}
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="battle-bar-section">
          <div className="battle-split-bar">
            <div style={{ flex: pA, background: colorA, height: '100%', borderRadius: '8px 0 0 8px', transition: 'flex 0.8s ease' }} />
            <div style={{ flex: pB, background: colorB, height: '100%', borderRadius: '0 8px 8px 0', transition: 'flex 0.8s ease' }} />
          </div>
          <div className="battle-bar-labels">
            <span style={{ color: colorA, fontWeight: 900, fontSize: 13 }}>{pA}%</span>
            <span style={{ color: 'rgba(148,163,184,0.6)', fontSize: 12 }}>{fmtNum(total)} votes</span>
            <span style={{ color: colorB, fontWeight: 900, fontSize: 13 }}>{pB}%</span>
          </div>
        </div>

        {voteError && <p className="battle-vote-error">{voteError}</p>}

        {cooldownSecs > 0 && (
          <div className="battle-cooldown-banner">
            ⏳ Credits refilling in <strong style={{ color: '#F97316' }}>{cooldownSecs}s</strong>
          </div>
        )}

        {isEnded && (
          <button className="battle-rematch-btn" onClick={handleRematch} disabled={rematchLoading || rematchDone}>
            {rematchDone ? '✓ Rematch Requested!' : rematchLoading ? 'Requesting…' : '🔄 Demand a Rematch'}
          </button>
        )}
      </div>

      {/* Comments toggle */}
      <button className="battle-comments-toggle" onClick={() => setShowComments(o => !o)}>
        <MessageCircle size={17} />
        Fan Talk
        <span className="battle-comments-count-badge">{comments.length}</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'rgba(148,163,184,0.5)' }}>{showComments ? '▲' : '▼'}</span>
      </button>

      {/* Comments section */}
      {showComments && (
        <div className="battle-comments-section">
          <div className="battle-comments-list">
            {comments.length === 0 ? (
              <div className="battle-empty-comments">
                <MessageCircle size={32} color="rgba(14,165,233,0.3)" />
                <p>No comments yet — start the convo!</p>
              </div>
            ) : (
              comments.map((c, i) => (
                <div key={c.id || i} className="battle-comment-row">
                  <div className="battle-comment-avatar" style={{ background: `hsl(${((commentAuthor(c).charCodeAt(0) || 0) * 37) % 360}, 60%, 40%)` }}>
                    {(commentAuthor(c)[0] || '?').toUpperCase()}
                  </div>
                  <div className="battle-comment-body">
                    <span className="battle-comment-author">{commentAuthor(c)}</span>
                    <span className="battle-comment-text">{commentText(c)}</span>
                  </div>
                </div>
              ))
            )}
            <div ref={commentsBottomRef} />
          </div>

          {token ? (
            <div className="battle-comment-input-row">
              <input
                className="battle-comment-input"
                placeholder="Add your take…"
                value={commentInput}
                onChange={e => setCommentInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleComment()}
                maxLength={280}
              />
              <button className="battle-comment-send" onClick={handleComment} disabled={!commentInput.trim() || posting}>
                <Send size={16} />
              </button>
            </div>
          ) : (
            <p className="battle-signin-prompt">Sign in to join the conversation</p>
          )}
        </div>
      )}
    </div>
  );
}
